/** @file Pixels of one packed sheet, composited from its blocks when every placed image has pixels. */
import { compositeSheet } from '@zilo/pose3d-export/pack/compositeSheet';
import type { Placement } from '@zilo/pose3d-export/pack/compositeSheet';
import type { ExportDeps } from '@zilo/pose3d-export/types/exportDeps';
import type { Image } from '@zilo/pose3d-export/types/imageIoShapes';
import type { PackBlock, PackedBlocks } from '@zilo/pose3d-export/types/packBlock';

/** The placements of `block` at `origin`, or undefined when an image lacks pixels. */
function placementsOf(block: PackBlock, origin: [number, number], images: NonNullable<ExportDeps['images']>): Placement[] | undefined {
  const out: Placement[] = [];
  for (const [tile, filled] of Object.entries(block.filled)) {
    const t = Number(tile);
    const pixels: Image | undefined = Object.hasOwn(images, filled.image) ? images[filled.image]?.pixels : undefined;
    if (pixels === undefined) return undefined;
    const at: [number, number] = block.extendX ? [origin[0], origin[1] + t * block.tile[1]] : [origin[0] + t * block.tile[0], origin[1]];
    out.push({ at, image: pixels, size: block.tile, slot: `${block.key}/${t}`, ...(filled.from === undefined ? {} : { from: filled.from }) });
  }
  return out;
}

/**
 * The pixels of packed sheet `name` (`size`): every block placed there with its tiles at their cells (sheet-backed
 * tiles cropped from their sheet image); undefined when any placed image has no pixels in `images`.
 */
export function packedSheetPixels(
  name: string, size: [number, number], blocks: PackBlock[], packed: PackedBlocks, images: NonNullable<ExportDeps['images']>,
): Image | undefined {
  const placements: Placement[] = [];
  for (const block of blocks) {
    const at = packed.origins[block.key];
    if (at?.sheet !== name) continue;
    const more = placementsOf(block, at.origin, images);
    if (more === undefined) return undefined;
    placements.push(...more);
  }
  return compositeSheet(size, placements);
}
